import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart3,
  BookOpen,
  Boxes,
  LayoutDashboard,
  LogOut,
  ShieldCheck,
  Wallet,
  Library,
  GraduationCap,
  MessagesSquare,
  Users,
  ScrollText,
  Coins,
  ShoppingBag,
  Trophy,
  Flame,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const STUDENT_NAV = [
  { to: "/app/student", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/app/student/courses", label: "My Courses", icon: BookOpen },
  { to: "/app/student/catalog", label: "Catalogue", icon: Library },
  { to: "/app/student/certificates", label: "Certificates", icon: ShieldCheck },
  { to: "/app/student/rewards", label: "Rewards", icon: Coins },
  { to: "/app/student/store", label: "Store", icon: ShoppingBag },
  { to: "/app/student/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/app/student/wallet", label: "Wallet", icon: Wallet },
  { to: "/app/student/tutor", label: "AI Tutor", icon: MessagesSquare },
];

const TEACHER_NAV = [
  { to: "/app/teacher", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/app/teacher/gradebook", label: "Gradebook", icon: ScrollText },
  { to: "/app/teacher/catalog", label: "Catalogue", icon: Library },
  { to: "/app/teacher/rewards", label: "Rewards", icon: Coins },
  { to: "/app/teacher/store", label: "Store", icon: ShoppingBag },
];

const ADMIN_NAV = [
  { to: "/app/admin", label: "Analytics", icon: BarChart3, end: true },
  { to: "/app/admin/users", label: "Users", icon: Users },
  { to: "/app/admin/audit", label: "Audit Trail", icon: ShieldCheck },
  { to: "/app/admin/catalog", label: "Catalogue", icon: Library },
  { to: "/app/admin/rewards", label: "Rewards", icon: Coins },
  { to: "/app/admin/store", label: "Store", icon: ShoppingBag },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const nav =
    user.role === "student"
      ? STUDENT_NAV
      : user.role === "teacher"
      ? TEACHER_NAV
      : ADMIN_NAV;

  return (
    <div className="min-h-screen bg-background brut">
      {/* Top bar */}
      <header className="glass-header sticky top-0 z-40 border-b border-border" data-testid="dashboard-header">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center bg-foreground text-background">
                <Boxes className="h-4 w-4" strokeWidth={2.5} />
              </div>
              <div className="leading-none">
                <div className="font-display text-sm font-extrabold tracking-tighter">
                  GROWTHINK
                </div>
                <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">
                  AI-CADEMY
                </div>
              </div>
            </Link>
            <div className="mono ml-4 hidden border-l border-border pl-4 text-[10px] uppercase tracking-widest text-muted-foreground md:block">
              {user.role} · console
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* MWZ pill */}
            {user.role === "student" && (
              <div className="hidden items-center gap-2 border border-foreground bg-background px-2.5 py-1 sm:flex" data-testid="header-mwz-pill">
                <Coins className="h-3.5 w-3.5 text-primary" />
                <span className="font-mono text-xs font-semibold">{(user.mwz_balance || 0).toLocaleString()}</span>
                <span className="mono text-[9px] uppercase tracking-widest text-muted-foreground">MWZ</span>
                {user.streak_days > 0 && (
                  <>
                    <span className="text-muted-foreground">·</span>
                    <span className="flex items-center gap-0.5 mono text-[10px] font-bold text-accent">
                      <Flame className="h-3 w-3" />
                      {user.streak_days}
                    </span>
                  </>
                )}
              </div>
            )}
            <div className="hidden text-right md:block">
              <div className="text-sm font-semibold leading-tight">{user.name}</div>
              <div className="mono text-[10px] text-muted-foreground">{user.email}</div>
            </div>
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="h-9 w-9 border border-foreground object-cover"
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center border border-foreground bg-foreground text-background">
                <GraduationCap className="h-4 w-4" />
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                logout();
                navigate("/");
              }}
              className="rounded-none"
              data-testid="dashboard-logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden w-60 shrink-0 border-r border-border bg-background md:block" data-testid="dashboard-sidebar">
          <nav className="sticky top-[57px] p-4">
            <ul className="space-y-1">
              {nav.map((n) => (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 border-l-2 px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "border-foreground bg-muted font-semibold"
                          : "border-transparent text-foreground/80 hover:bg-muted/60"
                      }`
                    }
                    data-testid={`sidebar-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <n.icon className="h-4 w-4" />
                    <span>{n.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Mobile bottom nav */}
        <nav className="fixed bottom-0 left-0 right-0 z-30 flex border-t border-foreground bg-background md:hidden">
          {nav.slice(0, 5).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[10px] ${
                  isActive ? "bg-foreground text-background" : "text-foreground/70"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              <span className="mono uppercase tracking-wider">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <main className="min-h-[calc(100vh-57px)] flex-1 pb-24 md:pb-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
